/**
 * <InstitutionalHero />
 *
 * Header gradient corporate réutilisable pour tous les onglets clés du
 * Super Admin Dashboard. Garantit un langage visuel uniforme :
 *   - Gradient slate-900 → couleur d'accent → couleur secondaire
 *   - Badge institutionnel (orné de bullets décoratifs)
 *   - Icône principale dans un container glassmorphism
 *   - Titre + sous-titre
 *   - Slot pour les boutons d'action (`actions`)
 *   - Grille de KPIs inline (`kpis`) sous le titre
 *
 * Utilisation :
 *   <InstitutionalHero
 *     icon={Key} accent="purple-900" secondary="indigo-900"
 *     badge="Sécurité d'accès" title="Identifiants & Accès"
 *     subtitle="Pilotage centralisé des comptes admin..."
 *     iconColor="text-yellow-300"
 *     actions={<Button>...</Button>}
 *     kpis={[ { label: 'Admins', value: 12, icon: Users, accent: 'bg-emerald-400/25' } ]}
 *   />
 */
import React from 'react';
import { ShieldCheck } from 'lucide-react';

const InstitutionalHero = ({
  icon: Icon,
  iconColor = 'text-yellow-300',
  badge,
  title,
  subtitle,
  actions,
  kpis,
  gradient = 'bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900',
}) => {
  return (
    <div className={`relative overflow-hidden rounded-2xl ${gradient} p-6 text-white shadow-xl`}>
      <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-yellow-400/10 blur-2xl" />
      <div className="absolute -left-10 bottom-0 w-56 h-56 rounded-full bg-cyan-400/10 blur-2xl" />
      <div className="relative flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          {Icon && (
            <div className="p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 shrink-0">
              <Icon className={`w-8 h-8 ${iconColor}`} />
            </div>
          )}
          <div className="min-w-0">
            {badge && (
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold uppercase tracking-widest bg-yellow-400/20 text-yellow-200 px-2 py-0.5 rounded-full border border-yellow-300/30">
                  {badge}
                </span>
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
              </div>
            )}
            <h2 className="text-2xl xl:text-3xl font-bold">{title}</h2>
            {subtitle && (
              <p className="text-sm text-blue-100 mt-1 max-w-2xl">{subtitle}</p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>
      {kpis && kpis.length > 0 && (
        <div className="relative mt-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {kpis.map((k, i) => (
            <HeroKpi key={i} {...k} />
          ))}
        </div>
      )}
    </div>
  );
};

const HeroKpi = ({ label, value, icon: Icon, accent = 'bg-white/15' }) => (
  <div className={`rounded-lg ${accent} backdrop-blur-sm p-3 border border-white/10`}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-blue-100 font-semibold">
      {Icon && <Icon className="w-3 h-3" />}
      <span className="truncate">{label}</span>
    </div>
    <div className="text-xl font-bold mt-0.5 tabular-nums text-white">{value}</div>
  </div>
);

export default InstitutionalHero;
